import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

// BLOQUE 48 — reemplaza el patrón nativo `<input list="x"><datalist id="x">`.
// Ese patrón tiene un bug conocido en Android Chrome: el cursor se resetea a la
// posición 0 en cada tecla, así que cada carácter nuevo se inserta al principio
// y el texto termina invertido (reporte real CEN: "Caño corrugado 3/4" se
// guardaba como "4/3 odagurroc oñaC"). Este componente ofrece el mismo
// autocompletar "escribí y sugiere" pero con un dropdown propio (sin `list`/
// `<datalist>`).
//
// BLOQUE 63 (2026-08-14) — el datalist era UN vector, no el único: el mismo
// síntoma reapareció en un pedido cargado DESPUÉS del despliegue del Bloque 48,
// sin ningún `list`/`<datalist>` en el código. Causa raíz real: este componente
// era 100% controlado por el padre (`value` venía de afuera) — cada tecla
// disparaba `onChange` → el padre reconstruía el formulario completo → todo
// volvía a renderizar (incluyendo un recorrido sin memoizar del catálogo de
// materiales) → React reescribía el atributo `value` del input DESPUÉS de que
// el teclado del celular ya había avanzado el cursor. En Android (Gboard y
// similares) el IME y esa reescritura tardía de `value` se desincronizan: el
// cursor queda en la posición 0 y el texto se escribe al revés. En escritorio
// no se nota porque el render es más rápido que la escritura.
//
// FIX: el input mantiene su propio estado local (`local`) y SIEMPRE se
// renderiza desde ese estado — nunca lo pisa un re-render del padre mientras el
// usuario tipea. La prop `value` solo se vuelve a copiar al estado local cuando
// cambia por una razón EXTERNA (elegir una sugerencia desde afuera, cargar un
// ítem existente, limpiar el formulario) — nunca como eco de lo que el propio
// componente le acaba de avisar al padre (se compara contra `lastEmitted`, la
// última cadena que este componente emitió). Además, el filtrado de
// sugerencias (que sí recorre el catálogo) se calcula con un pequeño debounce
// sobre una copia de `local`, para que escribir varias teclas seguidas no
// dispare un recorrido completo del catálogo por cada una — el texto en
// pantalla, en cambio, es instantáneo (viene de `local`, sin debounce).
export const AutocompleteInput = ({
  value,
  onChange,
  suggestions = [],
  placeholder = "",
  className = "",
  inputClassName = "",
  maxSuggestions = 8,
  required = false,
  multiline = false,
  ...rest
}) => {
  const [open, setOpen] = useState(false);
  const [local, setLocal] = useState(value || "");
  const [query, setQuery] = useState(value || "");
  const lastEmitted = useRef(value || "");
  const wrapRef = useRef(null);
  // Fix IU (2026-11-02): "En Pedido de Materiales y los demás apartados equivalentes, mostrar completa la descripción
  // de cada celda, sin truncarla ni perder edición/maquetado" (CEN). Con `multiline` el campo es un textarea de una
  // línea que crece hacia abajo con el texto: se lee completo y se sigue editando igual (Enter no agrega renglones).
  const campoRef = useRef(null);
  const ajustarAlto = () => {
    const el = campoRef.current;
    if (!multiline || !el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
  };
  useLayoutEffect(ajustarAlto);
  useEffect(() => {
    const el = campoRef.current;
    if (!multiline || !el || typeof ResizeObserver === "undefined") return undefined;
    // Si cambia el ancho (columna, ventana, modal que termina de abrirse) se vuelve a medir, en el cuadro siguiente.
    let ancho = -1, cuadro = 0;
    const ro = new ResizeObserver(() => {
      if (el.clientWidth === ancho) return;
      ancho = el.clientWidth;
      cancelAnimationFrame(cuadro);
      cuadro = requestAnimationFrame(ajustarAlto);
    });
    ro.observe(el);
    return () => { cancelAnimationFrame(cuadro); ro.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multiline]);

  // Sincroniza desde afuera SOLO si el cambio no fue generado por este mismo
  // componente (evita que el eco del padre pise lo que el usuario está tipeando).
  useEffect(() => {
    const incoming = value || "";
    if (incoming !== lastEmitted.current) {
      setLocal(incoming);
      setQuery(incoming);
      lastEmitted.current = incoming;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Debounce exclusivo del filtrado de sugerencias — el texto visible (`local`)
  // nunca espera este timer.
  useEffect(() => {
    const t = setTimeout(() => setQuery(local), 150);
    return () => clearTimeout(t);
  }, [local]);

  useEffect(() => {
    const onOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("touchstart", onOutside);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("touchstart", onOutside);
    };
  }, []);

  const commit = (v) => {
    setLocal(v);
    lastEmitted.current = v;
    onChange(v);
  };

  // Fix FS (2026-10-12): una sugerencia puede ser un texto o un objeto { value, label, hint }:
  // se muestra `label` (más `hint` al costado), se busca por sus palabras en cualquier orden y
  // al elegirla se escribe `value`.
  const filtered = useMemo(() => {
    const q = query || "";
    const palabras = q.toLowerCase().split(/\s+/).filter(Boolean);
    return (suggestions || [])
      .filter(Boolean)
      .filter((s) => (typeof s === "string" ? s : s.value).toLowerCase() !== q.toLowerCase())
      .filter((s) => {
        if (typeof s === "string") return !q || s.toLowerCase().includes(q.toLowerCase());
        const t = s.label.toLowerCase();
        return palabras.every((w) => t.includes(w));
      })
      .slice(0, maxSuggestions);
  }, [suggestions, query, maxSuggestions]);

  const campo = {
    ...rest,
    value: local,
    required,
    placeholder,
    autoComplete: "off",
    // Fix IU: en el textarea, un salto de línea pegado queda como espacio (la descripción sigue siendo una línea).
    onChange: (e) => { commit(multiline ? e.target.value.replace(/\s*\r?\n\s*/g, " ") : e.target.value); setOpen(true); },
    onFocus: () => setOpen(true),
    onBlur: () => commit(local),
    onKeyDown: (e) => {
      // Fix O (2026-08-31): Enter tipeando en el buscador disparaba el submit
      // del formulario que lo envuelve antes de elegir una sugerencia.
      if (e.key === "Enter") { e.preventDefault(); commit(local); setOpen(false); }
    },
  };
  return (
    <div className={`relative ${className}`} ref={wrapRef}>
      {multiline
        ? <textarea {...campo} ref={campoRef} rows={1} className={`${inputClassName} block resize-none overflow-hidden whitespace-pre-wrap break-words`} />
        : <input {...campo} className={inputClassName} />}
      {open && filtered.length > 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto min-w-[320px]">
          {/* Fix AY (2026-09-06): "en el despliegue no se lee toda la descripción,
              se tiene que poder leer todo" (Edson, 06/09). Cortadas en una línea,
              dos descripciones que empiezan igual eran indistinguibles. */}
          {filtered.map((s, idx) => {
            const valor = typeof s === "string" ? s : s.value;
            return (
              <button
                type="button"
                key={`${valor}-${idx}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { commit(valor); setQuery(valor); setOpen(false); }}
                className="w-full text-left px-2.5 py-1.5 text-xs hover:bg-blue-50 text-gray-700 whitespace-normal break-words leading-snug border-b border-gray-50 last:border-0"
              >
                {typeof s === "string" ? s : s.label}
                {typeof s !== "string" && s.hint && <span className="block text-[10px] font-semibold text-emerald-700">{s.hint}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
